import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {takeUntil} from "rxjs/operators";
import {CcBaseComponent} from '../../../crystal-elements/cc-base.component';
import {FormFieldConfig} from '../../../model/api/form-field-config';
import {FormFieldGroupConfig} from '../../../model/api/form-field-group-config';
import {InternalTradeOrderViewService} from '../../../services/internal/internal-trade-order-view.service';
import {CommonFieldService} from '../../../services/common/common-field.service';
import {UserInfoStorageService} from '../../../services/auth/user-info-storage.service';
import {FieldRecord} from '../../../model/api/field-record';
import {WebTradeOrderView} from "../../../model/internal/web-trade-order-view";
import {TableFilter} from "../../../model/api/table-filter";
import {CcFormComponent} from "../../../crystal-elements/form-dialog/form/cc-form.component";
import {Trade} from "../../../model/clearing/trade";
import {Order} from "../../../model/clearing/order";
import {Account} from "../../../model/clearing/account";
import {AccountDefinition} from "../../../model/customer/account-definition";
import {ClearingTableConfigService} from "../../../services/clearing/clearing-table-config.service";
import SHOW_OPTION = WebTradeOrderView.SHOW_OPTION;
import OPERATOR = TableFilter.OPERATOR;
import TYPE = FormFieldConfig.TYPE;

@Component({
  selector: 'app-user-view-form',
  styleUrls: ['../user-view/user-view.component.scss'],
  templateUrl: './user-view-form.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class UserViewFormComponent extends CcBaseComponent {

  // fields
  //fields
  defaultTemplate: string;
  templates: FieldRecord<any>[];
  showOptions: FieldRecord<any>[];
  commonFilterFields: FieldRecord<any>[] = [];
  orderFilterFields: FieldRecord<any>[] = [];
  tradeFilterFields: FieldRecord<any>[] = [];

  transPartKey: string = 'user-view';

  formLoading: boolean = false;

  formSubmitted: boolean = false;

  formConfig: FormFieldGroupConfig[] | any[] = [];

  dialogFormTitle: string = '';

  @Input()
  formRecord: WebTradeOrderView;

  loadingProgressValue: number = 100;

  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<any>();

  // tslint:disable-next-line:no-output-on-prefix
  @Output()
  onSave = new EventEmitter<any>();

  @ViewChild('ccForm')
  public ccForm: CcFormComponent;

  constructor(cdref: ChangeDetectorRef,
              private internalTradeOrderViewService: InternalTradeOrderViewService,
              private commonFieldService: CommonFieldService,
              private userInfoStorageService: UserInfoStorageService,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              private confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
    this.formRecord = new WebTradeOrderView();
    internalTradeOrderViewService.clearStorage();
    this.formRecord.userName = this.userInfoStorageService.getUsernameFromStorage();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected initConfigs() {
    super.initConfigs();

    const formFields = [];
    formFields.push(...[
      {
        field: 'viewName',
        required: true,
        column: 2,
        type: TYPE.TEXT,
      },
      {
        field: 'template',
        required: true,
        column: 2,
        type: TYPE.DROPDOWN,
        options: () => this.templates,
        // changed: () => this.refreshFormFieldFilters()
      },
      {
        field: 'visible',
        column: 3,
        type: TYPE.CHECKBOX,
      },
      {
        field: 'showOption',
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.showOptions,
      }]);

    const commonFilterField = UserViewFormComponent.getFiltersFormField('commonFilters', this.commonFilterFields,
      () => UserViewFormComponent.addFilter(this.ccForm, this.formRecord.commonFilters, this.commonFilterFields),
      (record: TableFilter) => UserViewFormComponent.deleteFilter(this.ccForm, this.formRecord.commonFilters, record));

    const orderFilterField = UserViewFormComponent.getFiltersFormField('orderFilters', this.orderFilterFields,
      () => UserViewFormComponent.addFilter(this.ccForm, this.formRecord.orderFilters, this.orderFilterFields),
      (record: TableFilter) => UserViewFormComponent.deleteFilter(this.ccForm, this.formRecord.orderFilters, record));
    orderFilterField['hidden'] = (record: WebTradeOrderView) => record.showOption == SHOW_OPTION.TRADE_ONLY;

    const tradeFilterField = UserViewFormComponent.getFiltersFormField('tradeFilters', this.tradeFilterFields,
      () => UserViewFormComponent.addFilter(this.ccForm, this.formRecord.tradeFilters, this.tradeFilterFields),
      (record: TableFilter) => UserViewFormComponent.deleteFilter(this.ccForm, this.formRecord.tradeFilters, record));
    tradeFilterField['hidden'] = (record: WebTradeOrderView) => record.showOption == SHOW_OPTION.ORDER_ONLY;

    this.formConfig = [
      {
        group: '',
        fields: formFields,
      },
      commonFilterField,
      orderFilterField,
      tradeFilterField
    ];
  }

  public static getFiltersFormField(field: string, filterFields: FieldRecord<any>[],
                                    addFilter: () => void, delFilter: (record: TableFilter) => void) {
    return {
      group: field,
      fields: [
        {
          field: field,
          class: 'with-title height-limited filter-table',
          column: 12,
          type: TYPE.TABLEFORMFIELD,
          richFieldConfig: {
            fields: [
              {
                group: '',
                fields: [
                  {
                    field: 'field',
                    type: TYPE.AUTOCOMPLETE,
                    optionValue: 'id',
                    options: () => filterFields,
                  },
                  {
                    field: 'matchMode',
                    type: TYPE.DROPDOWN,
                    optionValue: 'id',
                    options: (record) => UserViewFormComponent.getMatchModeOptions(record),
                    changed: (event, event2) => {
                      if (event2['matchMode'] == 'isNull' || event2['matchMode'] == 'isNotNull') {
                        event2['valueJs'] = 'NULL';
                      } else {
                        event2['valueJs'] = undefined;
                      }
                    },
                  },
                  {
                    field: 'valueJs',
                    type: TYPE.HYBRID,
                    hybridType: (record) => {
                      return UserViewFormComponent.getFieldType(record.field);
                    },
                    options: (record) => {
                      return UserViewFormComponent.getFieldOptions(record.field);
                    },
                    disabled: (record) => record.matchMode == 'isNull' || record.matchMode == 'isNotNull',
                    changed: (event, event2) => {
                      /* if(event&&JSON.stringify(event.checked))
                       event2['valueJs'] = event.checked;
                       else */
                      if (event && event.value)
                        event2['valueJs'] = event.value;
                      // else if(event2&&event2['valueJs'])
                      //   event2['valueJs'] = event2['valueJs'];
                      // else
                      //   event2.value = null;
                    },
                    // rightButton: {
                    //   icon: 'fa-solid fa-ban',
                    //   command: (event, record) => {
                    //     delFilter(record);
                    //   },
                    //   class: 'button-field-small'
                    // }
                  },
                  {
                    field: 'operator',
                    type: TYPE.DROPDOWN,
                    optionValue: 'id',
                    options: (record) => UserViewFormComponent.getOperatorOptions(record.field),
                    rightButton: {
                      icon: 'fa-solid fa-ban',
                      command: (event, record) => {
                        delFilter(record);
                      },
                      class: 'button-field-small'
                    }
                  }]
              },
            ]
          },
        }, {
          column: 12,
          type: TYPE.BUTTONS,
          class: 'button-add',
          buttons: [{
            icon: 'fas fa-trash-can',
            class: 'p-button-warning',
            command: event => {
              delFilter(undefined);
            }
          },
            {
              icon: 'fas fa-plus',
              class: 'p-button-success',
              command: event => {
                addFilter();
              }
            }]
        }]
    };
  }

  public refreshFormFieldFilters(): void {
    this.tradeFilterFields.splice(0);
    this.orderFilterFields.splice(0);
    this.commonFilterFields.splice(0);

    let tradeKeys = Object.keys(new Trade());
    tradeKeys.push(...Object.keys(new Account()).map(value => 'account.' + value));
    tradeKeys.sort();
    for (let field of tradeKeys) {
      if (!ClearingTableConfigService.unfilterableSortableColumns.includes(field)) {
        this.tradeFilterFields.push({
          id: field,
          text: field,
          value: field
        });
      }
    }
    let orderKeys = Object.keys(new Order());
    orderKeys.push(...Object.keys(new Account()).map(value => 'account.' + value));
    orderKeys.sort();
    for (let field of orderKeys) {
      if (!ClearingTableConfigService.unfilterableSortableColumns.includes(field)) {
        this.orderFilterFields.push({
          id: field,
          text: field,
          value: field
        });
      }
    }
    this.orderFilterFields.forEach(orderFilterField => {
      const filterField: FieldRecord<any> = this.tradeFilterFields.find(tradeFilterField => orderFilterField.id == tradeFilterField.id);
      if (filterField) {
        this.commonFilterFields.push(filterField);
      }
    });
    this.tradeFilterFields = [...this.tradeFilterFields];
    this.orderFilterFields = [...this.orderFilterFields];
    this.commonFilterFields = [...this.commonFilterFields];
    if (this.ccForm) {
      this.ccForm.reloadFieldValueMap();
    }
  }

  protected initData() {
    super.initData();

    this.loadingProgressValue = 0;
    this.showOptions = [];
    for (let key in SHOW_OPTION) {
      this.showOptions.push(new FieldRecord(key, key.toLowerCase().replace('_', ' ')))
    }
    //
    // this.filterMatchModes.splice(0);
    // for (let key in TableFilter.MATCH_MODE) {
    //   this.filterMatchModes.push(new FieldRecord(key, key))
    // }
    // this.filterMatchModes = [...this.filterMatchModes];
    //
    // this.filterOperators.splice(0);
    // for (let key in TableFilter.OPERATOR) {
    //   this.filterOperators.push(new FieldRecord(key, key))
    // }
    // this.filterOperators = [...this.filterOperators];
    // ;

    this.refreshFormFieldFilters();

    this.internalTradeOrderViewService.getDefaultTemplate().pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.defaultTemplate = apiValidation.item;
        if (this.formRecord && (!this.formRecord.id || this.formRecord.id < 0)) {
          this.formRecord.template = this.defaultTemplate
          // this.refreshFormFieldFilters();
        }
      } else {
        this.errorMessage(apiValidation.message);
      }
      this.loadingProgressValue += 100 / 2;
    });

    this.internalTradeOrderViewService.getTemplates().pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.templates = apiValidation.item ? [...this.getFields(apiValidation.item)] : [];
      } else {
        this.errorMessage(apiValidation.message);
      }
      this.loadingProgressValue += 100 / 2;
    });

    //init new record Object
  }

  onShow(): void {
    if (this.formRecord && (!this.formRecord.id || this.formRecord.id < 0)) {
      // this.formRecord = new WebTradeOrderView();
      // this.formRecord.userName = this.userInfoStorageService.getUsernameFromStorage();
      this.formRecord.template = this.defaultTemplate;
    }
    // this.refreshFormFieldFilters();
    if (this.formRecord.id > 0) {
      this.dialogFormTitle = this.getTranslation('common.table.edit_item') + ' '
        + this.getTranslation('page.' + this.transPartKey + '.item_name');
      if(!this.formRecord.commonFilters)
        this.formRecord.commonFilters = [];
      if(!this.formRecord.orderFilters)
        this.formRecord.orderFilters = [];
      if(!this.formRecord.tradeFilters)
        this.formRecord.tradeFilters = [];

      UserViewFormComponent.initFilters(this.formRecord.commonFilters);
      UserViewFormComponent.initFilters(this.formRecord.orderFilters);
      UserViewFormComponent.initFilters(this.formRecord.tradeFilters);
      this.ccForm.markForCheck();
    } else {
      this.dialogFormTitle = this.getTranslation('common.table.create_item') + ' '
        + this.getTranslation('page.' + this.transPartKey + '.item_name');
    }
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  save() {
    UserViewFormComponent.prepareFiltersForSave(this.formRecord.commonFilters);
    UserViewFormComponent.prepareFiltersForSave(this.formRecord.orderFilters);
    UserViewFormComponent.prepareFiltersForSave(this.formRecord.tradeFilters);
    this.internalTradeOrderViewService.saveRecord(this.formRecord).pipe(takeUntil(this.destroy$)).subscribe(validation => {
      if (validation.success) {
        this.closeDialogForm();
        this.onSave.emit(this.formRecord);
      } else {
        this.errorMessage(validation.message);
      }
    });
  }


  public static addFilter(ccForm: CcFormComponent, filters: TableFilter[], filterFields: FieldRecord<any>[]) {
    const nextFiltersFound = filterFields.filter(filterField => !filters?.find(filter => filterField.id == filter.field));
    var nextFilter = nextFiltersFound.length > 0 ? nextFiltersFound[0] : filterFields[0];
    if (nextFilter) {
      filters?.push({
        field: nextFilter.id,
        // matchMode: this.getMatchModeOptions(nextFilter.id)[0].id,
        operator: OPERATOR.and
      });
      if (ccForm)
        ccForm.reloadFieldValueMap();
    }
  }

  public static deleteFilter(ccForm: CcFormComponent, filters: TableFilter[], record) {
    if (record) {
      const existingValueIndex: number = filters?.findIndex(value => value == record);
      if (existingValueIndex >= 0) {
        filters?.splice(existingValueIndex, 1);
      }
    } else {
      filters?.splice(0);
    }
    if (ccForm)
      ccForm.reloadFieldValueMap();
  }

  // onFormRecordChanged() {
  //   this.refreshFormFieldFilters();
  // }

  public saveComplete() {
    this.internalTradeOrderViewService.clearStorageViews()
  }

  public static getFieldType(field): FormFieldConfig.TYPE {
    var fieldType = FormFieldConfig.TYPE.TEXT;
    if (['isGiveIn', 'isGiveUp', 'alreadyPaidElsewhere', 'avgPriceManaged', 'brokerageOverridden'
      , 'isChecked', 'isDisabled', 'isForceClear', 'jobOut', 'manualInvoiceRemoved'].includes(field)) {
      fieldType = FormFieldConfig.TYPE.CHECKBOX;
    } else if (['businessDate', 'clearingDate'].includes(field)) {
      fieldType = FormFieldConfig.TYPE.DATE;
    } else if (['creationDate', 'executionDate', 'modificationDate'].includes(field)) {
      fieldType = FormFieldConfig.TYPE.DATETIME;
    } else if (['volume', 'unmatchedVolume', 'strategyMissing', 'obLegRank'].includes(field)) {
      fieldType = FormFieldConfig.TYPE.INTEGER;
    } else if (['price', 'avgPrice', 'avgRndPrice', 'avgPriceResidual', 'avgPriceResidualAlloc', 'avgPriceResidualGroup', 'overrideAvgPrice'
      , 'strike', 'brokerageFee', 'brokerageRateAmount', 'overrideBrokerageFee', 'overrideBrokerageRateAmount'
      ].includes(field)) {
      fieldType = FormFieldConfig.TYPE.DECIMAL;
    } else if (field === 'side' || field === 'openClose' || field === 'initialOpenClose' || field === 'callPut') {
      fieldType = FormFieldConfig.TYPE.MULTISELECT;
    }
    return fieldType;
  }

  public static initFilters(filters: TableFilter[]): void {
    filters?.forEach(filter => {
      if (filter.value) {
        const fieldType = UserViewFormComponent.getFieldType(filter.field);
        if (fieldType == FormFieldConfig.TYPE.CHECKBOX) {
          filter['valueJs'] = filter.value === 'true';
        } /*else if (fieldType == FormFieldConfig.TYPE.DATE) {
          console.log("----> 1 " + JSON.stringify(filter))
        }*/ else if (fieldType == FormFieldConfig.TYPE.INTEGER) {
          filter['valueJs'] = parseInt(filter.value);
        } else if (fieldType == FormFieldConfig.TYPE.DECIMAL) {
          filter['valueJs'] = parseFloat(filter.value);
        } else if (fieldType == FormFieldConfig.TYPE.MULTISELECT) {
          filter['valueJs'] = filter.value.split(',');
        } else {
          filter['valueJs'] = filter.value;
        }
      }
    })
  }

  public static prepareFiltersForSave(filters: TableFilter[]): void {
    filters?.forEach(filter => {
      if (filter['valueJs'] != undefined) {
        const fieldType = UserViewFormComponent.getFieldType(filter.field);
        if (fieldType == FormFieldConfig.TYPE.CHECKBOX) {
          filter.value = filter['valueJs'] ? 'true' : 'false';
        } /*else if (fieldType == FormFieldConfig.TYPE.DATE) {
          console.log("----> 1 " + JSON.stringify(filter))
        } */ else if (fieldType == FormFieldConfig.TYPE.INTEGER) {
          filter.value = filter['valueJs'].toString();
        } else if (fieldType == FormFieldConfig.TYPE.DECIMAL) {
          filter.value = filter['valueJs'].toString();
        } else if (fieldType == FormFieldConfig.TYPE.MULTISELECT) {
          filter.value = filter['valueJs'].join(',');
        } else {
          filter.value = filter['valueJs'];
        }
      }
    })
  }

  public static getFieldOptions(field): FieldRecord<any>[] {
    const fieldOptions: FieldRecord<any>[] = [];
    if (field === 'side') {
      for (let key in Trade.Side) {
        fieldOptions.push(new FieldRecord(key, key))
      }
    } else if (field === 'openClose' || field === 'initialOpenClose') {
      for (let key in AccountDefinition.OpenClose) {
        fieldOptions.push(new FieldRecord(key, key))
      }
    } else if (field === 'callPut') {
      for (let key in Trade.CallPut) {
        fieldOptions.push(new FieldRecord(key, key))
      }
    }
    return fieldOptions;
  }

  public static getMatchModeOptions(filter: TableFilter): FieldRecord<any>[] {
    const fieldType = UserViewFormComponent.getFieldType(filter.field);
    const fieldOptions: FieldRecord<any>[] = [];

    if (fieldType == FormFieldConfig.TYPE.CHECKBOX) {
      fieldOptions.push(new FieldRecord(TableFilter.MATCH_MODE_TEXT.equals, TableFilter.MATCH_MODE_TEXT.equals.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()))
      fieldOptions.push(new FieldRecord(TableFilter.MATCH_MODE_TEXT.notEquals, TableFilter.MATCH_MODE_TEXT.notEquals.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()))
    } else if (fieldType == FormFieldConfig.TYPE.DATE || fieldType == FormFieldConfig.TYPE.DATETIME) {
      for (let key in TableFilter.MATCH_MODE_DATE) {
        fieldOptions.push(new FieldRecord(key, key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()))
      }
    } else if (fieldType == FormFieldConfig.TYPE.INTEGER
      || fieldType == FormFieldConfig.TYPE.DECIMAL) {
      for (let key in TableFilter.MATCH_MODE_NUMBER) {
        fieldOptions.push(new FieldRecord(key, key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()))
      }
    } else if (fieldType == FormFieldConfig.TYPE.MULTISELECT) {
      fieldOptions.push(new FieldRecord(TableFilter.MATCH_MODE_TEXT.in, TableFilter.MATCH_MODE_TEXT.in))
      fieldOptions.push(new FieldRecord(TableFilter.MATCH_MODE_TEXT.notIn, TableFilter.MATCH_MODE_TEXT.notIn.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()))
    } else {
      for (let key in TableFilter.MATCH_MODE_TEXT) {
        fieldOptions.push(new FieldRecord(key, key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()));
      }
    }

    if (!filter.matchMode || !fieldOptions.some(opt => opt.id == filter.matchMode)) {
      filter.matchMode = fieldOptions[0].id;
    }

    return fieldOptions;
  }

  public static getOperatorOptions(field): FieldRecord<any>[] {
    const fieldOptions: FieldRecord<any>[] = [];
    for (let key in TableFilter.OPERATOR) {
      fieldOptions.push(new FieldRecord(key, key));
    }
    return fieldOptions;
  }
}
